"use client";

import { useEffect } from "react";
import { initPostHog } from "@/lib/posthog";

// posthog-js and Meta's fbevents.js are the two biggest main-thread costs on a
// low-end phone, and neither needs to run before the visitor does anything:
// fbq() calls queue in the inline stub (root layout) and PostHog work queues
// via onPostHogReady, so deferring loses no events. Load on the first
// interaction, or — for passive readers — a while after `load`. The 6s fallback
// deliberately sits past Lighthouse's 5s quiet window so analytics never lands
// inside the measured TBT/TTI range.
const IDLE_DELAY_MS = 6000;

const AXON_SCRIPTS = [
  "https://s.axon.ai/pixel.js",
  "https://res4.applovin.com/p/l/loader.iife.js",
];

const INTERACTION_EVENTS: Array<keyof WindowEventMap> = [
  "pointerdown",
  "keydown",
  "touchstart",
  "scroll",
];

type Props = {
  loadMetaPixel: boolean;
};

export function DeferredThirdParty({ loadMetaPixel }: Props) {
  useEffect(() => {
    let fired = false;
    let timer: number | undefined;

    const fire = () => {
      if (fired) return;
      fired = true;
      cleanup();
      initPostHog();
      if (loadMetaPixel && !document.getElementById("fbevents-src")) {
        const script = document.createElement("script");
        script.id = "fbevents-src";
        script.async = true;
        script.src = "https://connect.facebook.net/en_US/fbevents.js";
        document.head.appendChild(script);
      }
      // AppLovin's two files, in AppLovin's order. They drain the axon() stub's operationQueue,
      // so everything queued since hydration (page_view included) is sent on arrival.
      for (const [index, src] of AXON_SCRIPTS.entries()) {
        const id = `axon-src-${index}`;
        if (document.getElementById(id)) continue;
        const script = document.createElement("script");
        script.id = id;
        script.async = true;
        script.src = src;
        document.head.appendChild(script);
      }
    };

    const arm = () => {
      timer = window.setTimeout(fire, IDLE_DELAY_MS);
    };

    const cleanup = () => {
      for (const event of INTERACTION_EVENTS) {
        window.removeEventListener(event, fire, { capture: true });
      }
      window.removeEventListener("load", arm);
      if (timer !== undefined) window.clearTimeout(timer);
    };

    for (const event of INTERACTION_EVENTS) {
      window.addEventListener(event, fire, { capture: true, passive: true, once: true });
    }
    if (document.readyState === "complete") arm();
    else window.addEventListener("load", arm, { once: true });

    return cleanup;
  }, [loadMetaPixel]);

  return null;
}
